import { useState, useCallback, useRef, useEffect } from "react";

import clsx from "clsx";

import Tag from "../Tag";
import FieldLabel from "../Field-Label";
import useKeyPress from "../utils/key-press";
import { Props } from "./types";

const TagsInput = ({
  name,
  label,
  labelTags,
  required,
  error,
  help,
  placeholder = "Type and press enter",
  selected,
  onChange,
}: Props) => {
  const [focussed, setFocussed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = useCallback(() => {
    const current = inputRef.current;
    if (current) {
      const value = current.value;
      if (value) {
        if (!selected.includes(value)) {
          onChange([...selected, value]);
        }
        current.value = "";
      }
    }
  }, [selected, onChange]);

  const removeTag = useCallback(
    (value: string) => {
      if (selected.includes(value)) {
        onChange(selected.filter((v) => v !== value));
      }
    },
    [selected, onChange]
  );

  // Pressing Enter should add current input to list.
  const enterPress = useKeyPress("Enter", inputRef);
  useEffect(() => {
    enterPress && addTag();
  });

  // Pressing Delete should remove last item from list.
  const backSpacePress = useKeyPress("Backspace", inputRef);
  const [itemsToDelete, setItemsToDelete] = useState(0);
  useEffect(() => {
    if (
      inputRef.current?.value === "" &&
      backSpacePress &&
      itemsToDelete === 0
    ) {
      // We want to remove items one by one.
      // Otherwise, selected changes, and hence this entire lot runs again before keyup is seen
      // To avoid this, we remove ONLY when itemsToDelete is 0
      setItemsToDelete(itemsToDelete + 1);
      removeTag(selected[selected.length - 1]);
      setItemsToDelete(itemsToDelete - 1);
    }
    if (!backSpacePress) {
      setItemsToDelete(0);
    }
  }, [backSpacePress, itemsToDelete, setItemsToDelete, selected, removeTag]);

  // Escape should reset the search box.
  const escapePress = useKeyPress("Escape", inputRef);
  useEffect(() => {
    if (escapePress) {
      const current = inputRef.current;
      if (current) {
        current.value = "";
      }
    }
  });

  const itemsToDisplay = focussed ? selected : selected.slice(0, 2);

  return (
    <>
      <div onFocus={() => setFocussed(true)} onBlur={() => setFocussed(false)}>
        <div
          className={clsx("flex", label ? "justify-between" : "justify-end")}
        >
          {label && (
            <FieldLabel
              name={name}
              label={label}
              focussed={focussed}
              error={!!error}
              required={required}
              tags={labelTags}
            />
          )}
          <span
            className="text-primary-main cursor-pointer"
            onClick={() => {
              onChange([]);
            }}
            role="button"
          >
            REMOVE ALL
          </span>
        </div>
        <div
          className={clsx(
            "flex",
            "border-b-2",
            "border-gray-300",
            "hover:border-gray-400",
            "focus:border-primary-main",
            "text-sm"
          )}
        >
          {itemsToDisplay.map((opt, index) => (
            <Tag key={`selected-item-${index}`}>
              <span className={"flex flex-wrap items-center"}>
                {opt}{" "}
                <span
                  className="ml-1 cursor-pointer"
                  role="button"
                  aria-label="Remove from Selection"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(opt);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-5 w-5 text-gray-600"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </span>
            </Tag>
          ))}
          {!focussed && selected.length > 2 && (
            <Tag>
              + {selected.length - 2} more item
              {selected.length > 3 ? "s" : ""}
            </Tag>
          )}
          <input
            id={name}
            name={name}
            ref={inputRef}
            placeholder={placeholder || "Enter a Value"}
            aria-label={
              typeof label === "string"
                ? label
                : label
                ? undefined
                : "Search Box"
            }
            className={clsx(
              "pl-1",
              "my-1",
              "w-full",
              "outline-none",
              "flex-1",
              "text-gray-800"
            )}
          />
        </div>
      </div>
      {help && (
        <div
          className={clsx(
            "text-xs",
            !!error ? "text-error-dark" : "text-gray-700"
          )}
        >
          {help}
        </div>
      )}
      {error && <div className="text-error-dark text-xs">{error}</div>}
    </>
  );
};

export default TagsInput;
